import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, RefreshCw, Check, X, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null); // null or plan object
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', price: '', interval: 'year', interval_days: 365,
    description: '', features: '', is_active: true, sort_order: 0,
  });

  const loadPlans = useCallback(async () => {
    try {
      const res = await api.getAdminPlans();
      setPlans(res.data);
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const openCreate = () => {
    setForm({ name: '', price: '', interval: 'year', interval_days: 365, description: '', features: '', is_active: true, sort_order: plans.length });
    setEditDialog({ mode: 'create' });
  };

  const openEdit = (plan) => {
    setForm({
      name: plan.name, price: plan.price, interval: plan.interval || 'year',
      interval_days: plan.interval_days || 365, description: plan.description || '',
      features: (plan.features || []).join('\n'), is_active: plan.is_active !== false,
      sort_order: plan.sort_order || 0,
    });
    setEditDialog({ mode: 'edit', id: plan.id });
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Name and price are required');
    setSaving(true);
    const data = {
      ...form,
      price: parseFloat(form.price),
      interval_days: parseInt(form.interval_days),
      sort_order: parseInt(form.sort_order),
      features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
    };
    try {
      if (editDialog.mode === 'create') {
        await api.createPlan(data);
        toast.success('Plan created');
      } else {
        await api.updatePlan(editDialog.id, data);
        toast.success('Plan updated');
      }
      setEditDialog(null);
      loadPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"?`)) return;
    try {
      await api.deletePlan(id);
      toast.success('Plan deleted');
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-sky-400" /></div>
  );

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Subscription Plans</h2>
          <p className="text-gray-400 text-sm mt-1">Manage membership tiers and pricing</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadPlans} className="text-gray-400 hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No plans yet. Create your first subscription plan.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(plan => (
            <Card key={plan.id} className={`border-gray-700 ${plan.is_active ? 'bg-gray-800/50' : 'bg-gray-900/30 opacity-60'}`} data-testid={`plan-card-${plan.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white">{plan.name}</CardTitle>
                  <div className="flex items-center gap-1">
                    <Badge className={plan.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-600/20 text-gray-400'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-3xl font-bold text-white">${plan.price?.toFixed(2)}</span>
                  <span className="text-gray-400 text-sm">/{plan.interval}</span>
                </div>
                {plan.description && <p className="text-gray-400 text-sm">{plan.description}</p>}
                {plan.features?.length > 0 && (
                  <ul className="space-y-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2 pt-2 border-t border-gray-700">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(plan)} className="text-sky-400 hover:text-sky-300 text-xs flex-1">
                    <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deletePlan(plan.id, plan.name)} className="text-red-400 hover:text-red-300 text-xs">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editDialog?.mode === 'create' ? 'New Plan' : 'Edit Plan'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-gray-400 text-xs">Plan Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="e.g. Annual Pass" data-testid="plan-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Price ($)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="plan-price-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Interval</Label>
                <Select value={form.interval} onValueChange={v => setForm({ ...form, interval: v, interval_days: v === 'year' ? 365 : v === 'month' ? 30 : 1 })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="year" className="text-white">Year</SelectItem>
                    <SelectItem value="month" className="text-white">Month</SelectItem>
                    <SelectItem value="one-time" className="text-white">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="Short description" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Features (one per line)</Label>
              <textarea value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-2 mt-1 text-sm min-h-[80px]" placeholder={"All live games\nFull replay library\nHD streaming"} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
              <Label className="text-gray-300 text-sm">Active (visible to users)</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setEditDialog(null)} className="text-gray-400">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="save-plan-btn">
                {saving ? 'Saving...' : 'Save Plan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPlans;
